import React from 'react';

/**
 * CountUp - animates from 0 to `value` once the element scrolls into view.
 *
 * Why this exists:
 *   Static round numbers ("25+", "1000+") read as AI-slop placeholders.
 *   A precise number that animates up on entry signals "real, tracked
 *   metric" - same vibe as a Stripe Atlas dashboard tile.
 *
 * Props:
 *   value      target integer (e.g. 65452)
 *   suffix     optional trailing string (e.g. "+")
 *   duration   animation duration in ms (default 1800)
 *   format     'comma' (default) renders 65,452. 'plain' renders 65452.
 *   className  passes through to the rendered <span>
 *
 * Behaviour:
 *   - Animates only once per mount.
 *   - Triggers when 25% of the element is in view (IntersectionObserver).
 *   - Skips animation entirely if user prefers-reduced-motion - just renders
 *     the final value statically.
 *   - Uses ease-out-quart so the count visibly decelerates near the end
 *     (a linear count looks robotic).
 */
const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

const formatComma = (n) => Math.round(n).toLocaleString('en-AU');

const CountUp = ({
  value,
  suffix = '',
  duration = 1800,
  format = 'comma',
  className = '',
  testId,
}) => {
  const elRef = React.useRef(null);
  const [display, setDisplay] = React.useState(0);
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    const el = elRef.current;
    if (!el) return undefined;

    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setDisplay(value);
      return undefined;
    }

    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const t0 = performance.now();
      const tick = (now) => {
        const elapsed = now - t0;
        const progress = Math.min(1, elapsed / duration);
        const eased = easeOutQuart(progress);
        setDisplay(value * eased);
        if (progress < 1) requestAnimationFrame(tick);
        else setDisplay(value);
      };
      requestAnimationFrame(tick);
    };

    // IntersectionObserver: kick off the animation once the element enters
    // 25% into view. We disconnect immediately after triggering.
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          start();
          io.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  const rendered = format === 'comma' ? formatComma(display) : Math.round(display).toString();

  return (
    <span
      ref={elRef}
      className={className}
      data-testid={testId}
      aria-label={`${formatComma(value)}${suffix}`}
    >
      {rendered}{suffix}
    </span>
  );
};

export default CountUp;
